import React, { createContext, useContext } from 'react';
import { useStream } from '@langchain/langgraph-sdk/react';
import { Client } from '@langchain/langgraph-sdk';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import supabase from '../lib/supabase';
import { LangGraphMessage } from '../types';

// Generate random ID utility function
const generateRandomId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Context type that matches the original ChatContext interface
interface LanggraphChatContextType {
  messages: LangGraphMessage[];
  isProcessing: boolean;
  sendUserMessage: (query: string) => void;
}

const LanggraphChatContext = createContext<LanggraphChatContextType | undefined>(undefined);

export const useLanggraphChat = () => {
  const context = useContext(LanggraphChatContext);
  if (!context) {
    throw new Error('useLanggraphChat must be used within a LanggraphChatProvider');
  }
  return context;
};

export const LanggraphChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get authentication context
  const { user, logout } = useAuth();
  const { language, labels } = useLanguage();
  
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Get current session token for authentication
  const [authHeaders, setAuthHeaders] = React.useState<Record<string, string>>({});
  const [client, setClient] = React.useState<Client | undefined>(undefined);

  // Update auth headers and client when session changes
  React.useEffect(() => {
    const updateAuthHeaders = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
          setAuthHeaders({});
          setClient(undefined);
          return;
        }
        
        if (session?.access_token) {
          const headers = {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          };
          setAuthHeaders(headers);
          
          // Create authenticated LangGraph client
          const authenticatedClient = new Client({
            apiUrl: process.env.REACT_APP_LANGGRAPH_API_URL || 'http://localhost:2024',
            defaultHeaders: headers,
          });
          setClient(authenticatedClient);
        } else {
          setAuthHeaders({});
          setClient(undefined);
        }
      } catch (err) {
        console.error('Error updating auth headers:', err);
        setAuthHeaders({});
        setClient(undefined);
      }
    };

    updateAuthHeaders();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.access_token) {
          const headers = {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          };
          setAuthHeaders(headers);
          
          // Create authenticated LangGraph client
          const authenticatedClient = new Client({
            apiUrl: process.env.REACT_APP_LANGGRAPH_API_URL || 'http://localhost:2024',
            defaultHeaders: headers,
          });
          setClient(authenticatedClient);
        } else {
          setAuthHeaders({});
          setClient(undefined);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Only initialize stream when we have a stable client and auth headers
  const streamConfig = React.useMemo(() => ({
    apiUrl: process.env.REACT_APP_LANGGRAPH_API_URL || 'http://localhost:2024',
    assistantId: process.env.REACT_APP_LANGGRAPH_ASSISTANT_ID || 'agent',
    messagesKey: 'messages',
    client: client, // Use authenticated client
    defaultHeaders: authHeaders, // Include authentication headers
    onError: (error: unknown) => {
      console.error('Stream error:', error);
      
      // Add more detailed logging for debugging
      if (error && typeof error === 'object') {
        console.log('Error details:', {
          status: (error as any).status,
          message: (error as any).message,
          url: (error as any).url,
          headers: authHeaders,
          hasClient: !!client,
          hasUser: !!user
        });
      }
      
      // Handle authentication errors
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status === 401) {
          console.error('Authentication failed - invalid or expired token');
          // Attempt to refresh session
          supabase.auth.refreshSession().then(({ error: refreshError }) => {
            if (refreshError) {
              console.error('Session refresh failed:', refreshError);
              logout(); // Force logout if refresh fails
            }
          });
          return;
        } else if (status === 403) {
          // Check if this is a history endpoint error (which we can safely ignore)
          const errorMessage = (error as any).message || '';
          const isHistoryError = errorMessage.includes('history') || 
                                (error as any).url?.includes('history') ||
                                errorMessage.includes('Authorization header required');
          
          if (isHistoryError) {
            console.log('Ignoring history endpoint 403 error - this is expected behavior');
            return;
          }
          
          console.error('Authorization failed - insufficient permissions');
          // Log additional context for 403 errors
          console.log('403 Error Context:', {
            user: user?.id,
            hasAuthHeaders: Object.keys(authHeaders).length > 0,
            authHeaderKeys: Object.keys(authHeaders),
            clientReady: !!client
          });
          return;
        }
      }
      
      // For other errors, retry with current messages
      // Note: We can't call stream.submit here due to circular dependency
      // The error will be handled by the calling code
    },
    onFinish: () => {
      // Stream finished successfully
    },
  }), [client, authHeaders, user, logout]);

  // Langgraph stream: state is { messages: LangGraphMessage[] }
  // Only initialize when we have a stable client to prevent auth issues
  const stream = useStream<{ messages: LangGraphMessage[] }>(
    client && Object.keys(authHeaders).length > 0 ? streamConfig : {
      apiUrl: process.env.REACT_APP_LANGGRAPH_API_URL || 'http://localhost:2024',
      assistantId: process.env.REACT_APP_LANGGRAPH_ASSISTANT_ID || 'agent',
      messagesKey: 'messages',
      client: undefined,
      defaultHeaders: {},
      onError: () => {},
      onFinish: () => {},
    }
  );

  // Log stream initialization (only when it actually changes)
  React.useEffect(() => {
    // Only log when authentication state changes, not on every message update
    if (client && Object.keys(authHeaders).length > 0) {
      console.log('Stream initialized with authentication:', {
        hasClient: !!client,
        hasAuthHeaders: Object.keys(authHeaders).length > 0,
        authHeaderKeys: Object.keys(authHeaders)
      });
    }
  }, [client, authHeaders]);

  // Filter out tool messages and get messages for the context
  const messages = (stream.messages || []).filter((msg: LangGraphMessage) => msg && msg.type !== 'tool');

  // Auto-initialize chat with agent introduction request
  React.useEffect(() => {
    // Only initialize once when we have no messages and authentication is ready
    if (!isInitialized && messages.length === 0 && user && language && labels && !stream.isLoading && client) {
      const currentDate = new Date().toLocaleDateString(language === 'da' ? 'da-DK' : 'en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      const languageName = language === 'da' ? 'dansk' : 'English';
      let initMessage = labels.initializationMessage.replace('{date}', currentDate);
      
      // Fix the language replacement - use the actual language name instead of the conditional
      if (language === 'da') {
        initMessage = initMessage.replace('dansk', languageName);
      } else {
        initMessage = initMessage.replace('English', languageName);
      }
      
      try {
        const newMessage: LangGraphMessage = {
          id: generateRandomId(),
          content: initMessage,
          type: 'human',
          timestamp: new Date().toISOString(),
        };
        
        stream.submit({ messages: [newMessage] });
        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing chat:', error);
      }
    } else {
      // Backup initialization with delay if conditions are almost met but client is not ready
      if (!isInitialized && messages.length === 0 && user && language && labels && !client) {
        setTimeout(() => {
          if (!isInitialized && messages.length === 0) {
            // Trigger the effect again by updating a state
            setIsInitialized(false);
          }
        }, 1000);
      }
    }
  }, [isInitialized, messages.length, user, language, labels, stream.isLoading, client, stream]);

  // Send user message
  const sendUserMessage = (query: string) => {
    try {
      const newMessage: LangGraphMessage = {
        id: generateRandomId(),
        content: query,
        type: 'human',
        timestamp: new Date().toISOString(),
      };
      const updatedMessages = [...(stream.messages || []), newMessage];
      stream.submit({ messages: updatedMessages });
    } catch (error) {
      console.error('Error sending user message:', error);
      stream.submit({ messages: stream.messages || [] });
    }
  };

  const value: LanggraphChatContextType = {
    messages,
    isProcessing: stream.isLoading,
    sendUserMessage,
  };

  return (
    <LanggraphChatContext.Provider value={value}>
      {children}
    </LanggraphChatContext.Provider>
  );
}; 